import React, { useCallback, useEffect, useMemo, useState } from 'react';
import httpClient from '../../../../services/httpClient';
import { actualizarDocentesCalificados } from '../../../../services/mallaService';
import { showToast } from '../../../../utils/showToast';

const normalizeModalidad = (value) =>
  (value || 'presencial').toString().trim().toLowerCase();
const normalizeActivo = (value) => value === 1 || value === true;
const normalizeHoras = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};
const parseHoras = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Docentes que pueden dictar esta materia en este curso. Ya no hay un
// "preferente" fijo por select — todo se maneja parejo aquí, guardado en
// cursos_materias_docentes. Si historicamente ya habia un docente_id
// asignado en la fila, arranca marcado (para no perder ese dato), pero se
// puede desmarcar igual que cualquier otro.
function ModalDocentesCalificados({ item, docentes, onClose, onGuardado }) {
  const [seleccionados, setSeleccionados] = useState(() => {
    const ids = (item.docentes_calificados || []).map((d) => String(d.id));
    if (item.docente_id && !ids.includes(String(item.docente_id))) {
      ids.push(String(item.docente_id));
    }
    return ids;
  });
  const [saving, setSaving] = useState(false);

  if (!item) return null;

  const toggle = (docenteId) => {
    const idStr = String(docenteId);
    setSeleccionados((prev) =>
      prev.includes(idStr) ? prev.filter((id) => id !== idStr) : [...prev, idStr]
    );
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await actualizarDocentesCalificados(item.id, seleccionados);
      showToast({ type: 'success', message: 'Docentes actualizados.' });
      onGuardado?.();
      onClose();
    } catch (err) {
      showToast({ type: 'danger', message: err.message || 'No se pudo guardar.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title fw-bold">
              Docentes — {item.materia_nombre}
            </h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-3">
              Marca quién puede dictar esta materia en este curso. Se usa al asignar clases
              puntuales en el cronograma.
            </p>
            {!docentes.length ? (
              <p className="text-muted small mb-0">No hay docentes registrados.</p>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {docentes.map((docente) => {
                  const fullName = `${docente.nombres || ''} ${docente.apellidos || ''}`.trim();
                  return (
                    <div className="form-check" key={docente.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`cal-${item.id}-${docente.id}`}
                        checked={seleccionados.includes(String(docente.id))}
                        onChange={() => toggle(docente.id)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor={`cal-${item.id}-${docente.id}`}>
                        {fullName || `Docente #${docente.id}`}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MallaList({
  malla,
  loading,
  canCreateMalla,
  canUpdateMalla,
  canDeleteMalla,
  savingCambios = false,
  onAgregar,
  onEliminar,
  onGuardarCambios,
  onRefresh,
}) {
  const minRows = 7;
  const maxHeight = '408px';
  const showAddButton = Boolean(canCreateMalla && onAgregar);

  const [docentes, setDocentes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [gestionarCalificados, setGestionarCalificados] = useState(null);

  const fetchDocentes = useCallback(async () => {
    try {
      const response = await httpClient.get('/api/docentes?activo=true');
      setDocentes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error al obtener docentes:', error);
    }
  }, []);

  useEffect(() => {
    if (!canUpdateMalla) return;
    fetchDocentes();
  }, [canUpdateMalla, fetchDocentes]);

  useEffect(() => {
    setDrafts({});
  }, [malla]);

  const updateDraft = useCallback((id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  }, []);

  const getOriginal = useCallback(
    (row) => ({
      horas_malla: normalizeHoras(row.horas_malla),
      modalidad: normalizeModalidad(row.modalidad),
      activo: normalizeActivo(row.activo),
    }),
    []
  );

  const getCurrentValue = useCallback(
    (row, field) => {
      if (drafts[row.id] && drafts[row.id][field] !== undefined) {
        return drafts[row.id][field];
      }
      return getOriginal(row)[field];
    },
    [drafts, getOriginal]
  );

  const isRowDirty = useCallback(
    (row) => {
      const original = getOriginal(row);
      const currentModalidad = getCurrentValue(row, 'modalidad');
      const currentActivo = getCurrentValue(row, 'activo');
      const currentHoras = parseHoras(getCurrentValue(row, 'horas_malla'));
      const originalHoras = parseHoras(original.horas_malla);

      return (
        original.modalidad !== currentModalidad ||
        original.activo !== currentActivo ||
        originalHoras !== currentHoras
      );
    },
    [getCurrentValue, getOriginal]
  );

  const changes = useMemo(
    () =>
      malla
        .filter((row) => isRowDirty(row))
        .map((row) => ({
          id: row.id,
          horas_malla: getCurrentValue(row, 'horas_malla'),
          modalidad: getCurrentValue(row, 'modalidad'),
          activo: getCurrentValue(row, 'activo'),
        })),
    [getCurrentValue, isRowDirty, malla]
  );

  const hasChanges = changes.length > 0;

  const handleGuardarCambios = useCallback(async () => {
    if (!onGuardarCambios) return;
    const ok = await onGuardarCambios(changes);
    if (ok) {
      setDrafts({});
    }
  }, [changes, onGuardarCambios]);

  const handleRefresh = useCallback(() => {
    if (!onRefresh) return;
    if (hasChanges) {
      const ok = window.confirm('Hay cambios sin guardar. Deseas descartarlos?');
      if (!ok) return;
    }
    onRefresh();
  }, [hasChanges, onRefresh]);

  const header = (
    <div className="mb-3 pb-3 border-bottom">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
        <h6 className="mb-0 fw-600">Materias en la Malla</h6>
        <div className="d-flex align-items-center gap-2">
          {showAddButton && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={onAgregar}
            >
              <i className="bi bi-plus-circle me-2" />
              Agregar Materia
            </button>
          )}
          {canUpdateMalla && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleGuardarCambios}
              disabled={!hasChanges || savingCambios || loading}
            >
              <i className="bi bi-save me-2" />
              {savingCambios ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleRefresh}
              title="Actualizar"
              disabled={loading || savingCambios}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          )}
        </div>
      </div>
      {canUpdateMalla && (
        <small className="text-muted">Recuerda guardar los cambios.</small>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!malla.length) {
    return (
      <div className="management-card">
        {header}
        <div className="management-empty-state">
          <div className="empty-icon">
            <i className="bi bi-inbox"></i>
          </div>
          <p className="empty-title">No hay materias en la malla</p>
          <p className="empty-description">Comienza agregando materias a este curso</p>
        </div>
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - malla.length);

  return (
    <div className="management-card">
      {header}

      <div className="management-table-wrapper malla-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table malla-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '20%' }}>Materia</th>
              <th style={{ width: '10%' }}>Sigla</th>
              <th style={{ width: '8%' }}>Color</th>
              <th style={{ width: '20%' }}>Docente</th>
              <th style={{ width: '12%' }}>Modalidad</th>
              <th style={{ width: '10%' }}>Horas</th>
              <th style={{ width: '8%' }}>Estado</th>
              <th style={{ width: '7%' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {malla.map((item, idx) => {
              const modalidadValue = getCurrentValue(item, 'modalidad');
              const horasValue = getCurrentValue(item, 'horas_malla');
              const activoValue = getCurrentValue(item, 'activo');

              return (
                <tr key={item.id}>
                  <td>{idx + 1}</td>
                  <td className="fw-500">{item.materia_nombre}</td>
                  <td>{item.sigla || '-'}</td>
                  <td>
                    {item.color_hex && (
                      <div
                        className="malla-color-swatch"
                        style={{ backgroundColor: item.color_hex }}
                        title={item.color_hex}
                      ></div>
                    )}
                  </td>
                  <td>
                    {canUpdateMalla ? null : (
                      item.docente_nombre || <span className="text-muted">Sin asignar</span>
                    )}
                    {canUpdateMalla && (() => {
                      const idsUnicos = new Set((item.docentes_calificados || []).map((d) => String(d.id)));
                      if (item.docente_id) idsUnicos.add(String(item.docente_id));
                      const totalDocentes = idsUnicos.size;
                      return (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary mt-1"
                          onClick={() => setGestionarCalificados(item)}
                          title="Marcar que docentes pueden dictar esta materia"
                        >
                          <i className="bi bi-people me-1" />
                          {totalDocentes ? `${totalDocentes} docente${totalDocentes === 1 ? '' : 's'}` : 'Agregar docentes'}
                        </button>
                      );
                    })()}
                  </td>
                  <td>
                    {canUpdateMalla ? (
                      <select
                        className="form-select form-select-sm malla-inline-select"
                        value={modalidadValue}
                        onChange={(e) =>
                          updateDraft(item.id, 'modalidad', e.target.value)
                        }
                        disabled={savingCambios}
                      >
                        <option value="presencial">Presencial</option>
                        <option value="online">Online</option>
                      </select>
                    ) : (
                      <span>{modalidadValue === 'online' ? 'Online' : 'Presencial'}</span>
                    )}
                  </td>
                  <td>
                    {canUpdateMalla ? (
                      <input
                        type="number"
                        min="0"
                        max="999.99"
                        step="0.5"
                        className="form-control form-control-sm malla-inline-input"
                        value={horasValue}
                        onChange={(e) =>
                          updateDraft(item.id, 'horas_malla', e.target.value)
                        }
                        disabled={savingCambios}
                      />
                    ) : (
                      `${item.horas_malla || 0}h`
                    )}
                  </td>
                  <td>
                    {canUpdateMalla ? (
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={Boolean(activoValue)}
                          onChange={(e) =>
                            updateDraft(item.id, 'activo', e.target.checked)
                          }
                          disabled={savingCambios}
                        />
                      </div>
                    ) : (
                      <span
                        className={`badge ${item.activo ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {item.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {canDeleteMalla && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onEliminar(item)}
                          title="Eliminar"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`malla-empty-${idx}`} className="table-empty-row" aria-hidden="true">
                  <td colSpan={9}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {gestionarCalificados && (
        <ModalDocentesCalificados
          item={gestionarCalificados}
          docentes={docentes}
          onClose={() => setGestionarCalificados(null)}
          onGuardado={onRefresh}
        />
      )}
    </div>
  );
}
